import * as Sentry from '@sentry/react';
import React from 'react';

import { monitoring } from '@/lib/monitoring/monitoring';

import { useChunkErrorRecovery } from '@/components/error-handling/hooks/use-chunk-error-recovery';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { DatadogErrorBoundary } from '@/components/error-handling/datadog-error-boundary';

interface ErrorFallbackProps {
  error: Error;
  resetError: () => void;
}

const DefaultErrorFallback: React.FC<ErrorFallbackProps> = ({ error, resetError }) => {
  // Chunk-load failures are recovery actions, not user-facing faults — never
  // display alarming copy. Keep the same full-screen container so layout
  // doesn't shift while the silent reload is in flight.
  if (useChunkErrorRecovery(error)) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-background px-4 py-12">
        <Spinner size="large" />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background px-4 py-12">
      <div className="w-full max-w-md space-y-8 text-center">
        <div className="space-y-4">
          <svg
            className="mx-auto h-16 w-16 text-destructive"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <h2 className="text-3xl font-bold text-foreground">Something went wrong</h2>
          <p className="text-foreground/90">
            An unexpected error occurred. Our team has been notified and is working on a fix.
          </p>
          {import.meta.env.DEV && (
            <details className="mt-4 text-left">
              <summary className="cursor-pointer text-sm text-muted-foreground">Error details</summary>
              <pre className="mt-2 overflow-auto rounded border border-destructive/20 bg-destructive/10 p-2 text-xs text-destructive">
                {error.message}
                {error.stack}
              </pre>
            </details>
          )}
        </div>
        <div className="space-y-3">
          <Button
            onClick={resetError}
            className="w-full"
          >
            Try Again
          </Button>
          <Button
            variant="outline"
            onClick={() => (window.location.href = '/')}
            className="w-full"
          >
            Go to Dashboard
          </Button>
        </div>
      </div>
    </div>
  );
};

interface MonitoringErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ComponentType<ErrorFallbackProps>;
  showDialog?: boolean;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

export function MonitoringErrorBoundary({
  children,
  fallback = DefaultErrorFallback,
  showDialog = false,
  onError,
}: MonitoringErrorBoundaryProps) {
  const provider = monitoring.getCurrentProvider();

  if (provider === 'sentry' || provider === 'both') {
    const sentryFallback = ({
      error,
      resetError,
    }: {
      error: unknown;
      componentStack: string;
      eventId: string;
      resetError: () => void;
    }) => {
      const FallbackComponent = fallback;
      // Convert unknown error to Error type for our fallback component
      const errorObj = error instanceof Error ? error : new Error(String(error));
      return <FallbackComponent error={errorObj} resetError={resetError} />;
    };

    const sentryOnError = onError
      ? (error: unknown, componentStack: string, _eventId: string) => {
          // Convert to React's ErrorInfo format for compatibility
          const errorInfo: React.ErrorInfo = { componentStack };
          onError(error as Error, errorInfo);
        }
      : undefined;

    const sentryWrapped = (
      <Sentry.ErrorBoundary
        fallback={sentryFallback}
        showDialog={showDialog}
        onError={sentryOnError}
      >
        {children}
      </Sentry.ErrorBoundary>
    );

    if (provider === 'both') {
      return <DatadogErrorBoundary>{sentryWrapped}</DatadogErrorBoundary>;
    }

    return sentryWrapped;
  }

  if (provider === 'datadog') {
    return <DatadogErrorBoundary>{children}</DatadogErrorBoundary>;
  }

  return <BasicErrorBoundary fallback={fallback}>{children}</BasicErrorBoundary>;
}

class BasicErrorBoundary extends React.Component<
  {
    children: React.ReactNode;
    fallback: React.ComponentType<ErrorFallbackProps>;
  },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: any) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Error caught by BasicErrorBoundary:', error, errorInfo);
  }

  render() {
    if (this.state.hasError && this.state.error) {
      const FallbackComponent = this.props.fallback;
      return (
        <FallbackComponent
          error={this.state.error}
          resetError={() => this.setState({ hasError: false, error: null })}
        />
      );
    }

    return this.props.children;
  }
}

export { DefaultErrorFallback };
